import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SelectDropdown } from '../SelectDropdown';
import { CheckboxSelect } from '../CheckboxSelect';
import { arrayHasItems } from '../../helpers/common';
import { ConfrimModal } from '../ConfrimModal';

export const BudgetLineItemEditModal = ({
    isOpen,
    onClose,
    options,
    forecastValues,
    rollupItems,
    selectedRPItems,
    budgetLineItemId, 
    selectedForecast,
    forecastValue,
    type, 
    code,
    name,
    categoryId,
    onAccept,
    onDelete,
    isDeleteLoading
}) => {

    const [currentCode, setCurrentCode] = useState(code);
    const [currentName, setCurrentName] = useState(name);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedType, setSelectedType] = useState(type);
    const [selectedForecastValue, setSelectedForecastValue] = useState(forecastValue);
    const [selectedRollUpItems, setSelectedRollUpItems] = useState([]);
    const [deleteConfirmIsOpen, setDeleteConfirmIsOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setDeleteConfirmIsOpen(false);
            return;
        }
        if (categoryId) {
            setSelectedCategory(categoryId);
        }
        if (forecastValue) {
            setSelectedForecastValue(forecastValue);
        }
        setSelectedRollUpItems(selectedRPItems || []);
    }, [isOpen]);

    const handleAcceptActions = async () => {
        await onAccept?.({
            budgetLineItemId,
            code: currentCode,
            name: currentName,
            selectedType,
            selectedCategory,
            selectedForecastValue,
            selectedRollUpItems
        });
    }

    const handleClose = () => {
        onClose();
    };

    const handleDeleteActions = () => {
        setDeleteConfirmIsOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (onDelete) {
            await onDelete();
        }
        setDeleteConfirmIsOpen(false);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmIsOpen(false);
    };

    // Handle Esc key press
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    aria-hidden="true"
                    onClick={handleClose}
                ></div>
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                <div 
                    className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 max-h-[90vh]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bg-blue-50 px-6 py-4 border-b border-blue-200 rounded-t-lg">
                        <h2 className="text-xl font-semibold text-blue-900 text-left">Edit Budget Line Item</h2>
                        <p className="text-sm text-blue-700 mt-1 text-left">Enter the details for the budget account</p>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700 text-left">
                                    Code
                                </label>
                                <div>
                                    <input
                                        type="text"
                                        value={currentCode}
                                        onChange={(e) => {
                                            setCurrentCode(e.target.value)
                                        }}
                                        className="w-full p-2.5 shadow-sm border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700 text-left">
                                    Name
                                </label>
                                <div>
                                    <input
                                        type="text"
                                        value={currentName}
                                        onChange={(e) => {
                                            setCurrentName(e.target.value)
                                        }}
                                        className="w-full p-2.5 shadow-sm border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700 text-left">
                                    Category
                                </label>
                                <div>
                                    <SelectDropdown
                                        key={`${categoryId}_${selectedCategory}`}
                                        options={options}
                                        value={selectedCategory}
                                        onChange={setSelectedCategory}
                                        itemValueClassname="px-2 py-1 text-sm text-left"
                                        selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
                                        selectClassname="!bg-white !px-3 !py-2 !border-input"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700 text-left">
                                    Type
                                </label>
                                <div>
                                    <SelectDropdown
                                        key={`${categoryId}_${selectedCategory}_${selectedForecast}`}
                                        options={[
                                            { label: "Editable", value: "EDITABLE" },
                                            { label: "Calculated", value: "CALCULATED" },
                                            { label: "Forecast Value", value: "FORECAST_VALUE" },
                                        ]}
                                        value={selectedType}
                                        onChange={setSelectedType}
                                        itemValueClassname="px-2 py-1 text-sm text-left"
                                        selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
                                        selectClassname="!bg-white !px-3 !py-2 !border-input"
                                    />
                                </div>
                            </div>
                        </div>
                        {selectedType !== "FORECAST_VALUE" && (
                            <div className="flex flex-col gap-2 mt-4 relative">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-gray-700 text-left">
                                        Roll-Up
                                    </label>
                                    {arrayHasItems(selectedRollUpItems) && (
                                        <div className="text-xs text-blue-700 text-left border border-blue-500 rounded-md px-2 py-0.5 leading-none">
                                            Has roll-up items
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <CheckboxSelect
                                        key={`${categoryId}_${selectedCategory}`}
                                        options={(rollupItems || []).filter((item) => item?.value !== budgetLineItemId)}
                                        selectedOptions={selectedRollUpItems}
                                        onChange={setSelectedRollUpItems}
                                        placeholder="Select related line items"
                                    />
                                </div>
                            </div>
                        )}
                        {selectedType === "FORECAST_VALUE" && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 ">
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm font-medium text-gray-700 text-left">
                                            Values
                                        </label>
                                        <div>
                                            <SelectDropdown
                                                key={`${categoryId}_${selectedForecastValue}_${selectedForecast}`}
                                                disabled={!selectedForecast}
                                                // options={forecastValues.find((forecast) => forecast.id === selectedForecast)?.options || []}
                                                options={forecastValues}
                                                value={selectedForecastValue}
                                                onChange={setSelectedForecastValue}
                                                itemValueClassname="px-2 py-1 text-sm text-left"
                                                selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
                                                selectClassname="!bg-white !px-3 !py-2 !border-input"
                                            />
                                        </div>
                                    </div>
                            </div>
                        )}
                        <div className="flex justify-between gap-3 mt-6">
                            <div>
                                <button
                                    type="button"
                                    className="ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors h-10 px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
                                    onClick={handleDeleteActions}
                                    disabled={isDeleteLoading}
                                >
                                    Delete
                                </button>
                            </div>
                            <div className='flex justify-end gap-3'>
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 border h-10 px-4 py-2 hover:bg-gray-100"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors h-10 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                                    onClick={handleAcceptActions}
                                >
                                    Edit Line Item
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {createPortal(modalContent, document.body)}
            <ConfrimModal
                isOpen={deleteConfirmIsOpen}
                title="Are you sure want to delete this budget line item?"
                isLoading={isDeleteLoading || false}
                confirmButtonTitle="Delete"
                submitClick={handleDeleteConfirm}
                cancelClick={handleDeleteCancel}
            />
        </>
    );
};